import {
  CatalogTEObject,
  DedicatedGroup,
  FrontendRegistration,
  FrontendRegistrationGroup,
  TTEObject,
  RegistrationActivity,
} from "@timeedit/registration-schemas";
import {
  Mapping,
  RangeReduce,
  UnsafeRecord,
  checkIsConflict,
  hasTime,
  isDefined,
} from "@timeedit/registration-shared";
import {
  EAppProperties,
  EApplicationObjectTypeGroup,
} from "@timeedit/types/lib/enums";
import dayjs from "dayjs";
import { Views } from "react-big-calendar";
import { nbrOfEventColors } from "../../../assets/styles/variables";
import { CalendarEvent } from "../../utils/events";
import { ConflictNotification } from "../Conflict/index";
import { RegistrationRequiredTranslations } from "./types";

export { RangeReduce };

export type SelectOptions = keyof ReturnType<typeof selectOptions>;

type SelectOptionsReturn = {
  readonly work_week: "work_week";
  readonly week: "week";
};
export function selectOptions(): SelectOptionsReturn {
  return {
    work_week: Views.WORK_WEEK,
    week: Views.WEEK,
  } as const;
}

/**
 * @param value - the value to be limited
 * @param min - inclusive minimum value
 * @param max - inclusive maximum balue
 * @returns
 * limited value
 */
export function limitedToRange(
  value: number,
  min: number,
  max: number
): number {
  return Math.max(Math.min(value, max), min);
}

export function getGroupName(
  group: FrontendRegistrationGroup,
  mapping: Mapping
): string {
  return mapping.parse("groupNumber", group.teObject);
}

type PresentObject = (object: CatalogTEObject) => string;

function presentLabel(mapping: Mapping): PresentObject {
  return (object: CatalogTEObject) => {
    const typeGroup = (mapping.getObjectTypeKeysById(object.types[0])[0] ??
      "NONE") as EApplicationObjectTypeGroup;

    return mapping.string(
      {
        objectTypeGroup: typeGroup,
        appProperty: EAppProperties.LABEL,
      },
      object
    );
  };
}

export function presentCategory(
  mapping: Mapping
): (dedicated: DedicatedGroup) => string {
  return (dedicated: DedicatedGroup) => {
    if (dedicated.kind === "category") {
      return dedicated.data
        .map(
          (category) => `${mapping.fieldname(category.id)} / ${category.value}`
        )
        .join(", ");
    }
    return "";
  };
}

type PresentRelationReturn = (
  dedicated: DedicatedGroup,
  fetchedObjects: UnsafeRecord<string, TTEObject>
) => string;
export function presentRelation(mapping: Mapping): PresentRelationReturn {
  return (
    dedicated: DedicatedGroup,
    fetchedObjects: UnsafeRecord<string, TTEObject>
  ) => {
    if (dedicated.kind === "relation") {
      const foundRelations: TTEObject[] = [];
      dedicated.data.forEach((data) => {
        const obj = fetchedObjects[data.id];
        if (isDefined(obj)) {
          foundRelations.push(obj);
        }
      });

      if (foundRelations.length > 0) {
        return foundRelations.map(presentLabel(mapping)).join(", ");
      }
    }
    return "";
  };
}

type CreateGroupNumbersProps = {
  events: CalendarEvent[];
  mapping: Mapping;
  groups: FrontendRegistration["groups"];
};

export function createGroupNumbers({
  events,
  mapping,
  groups,
}: CreateGroupNumbersProps): string[] {
  const sortedEvents = sortEventsOnGroupNumber({ events, groups, mapping });
  return sortedEvents.reduce<string[]>((groupNumbers, event) => {
    const group = groups[event.data.groupId];
    if (!isDefined(group)) return groupNumbers;

    const groupNumber = getGroupName(group, mapping);
    if (groupNumbers.includes(groupNumber) || groupNumber === "") {
      return groupNumbers;
    }
    return [...groupNumbers, groupNumber];
  }, []);
}

type SortEventsOnGroupNumber = {
  groups: FrontendRegistration["groups"];
  events: CalendarEvent[];
  mapping: Mapping;
  reverse?: boolean;
};

export function sortEventsOnGroupNumber({
  events,
  groups,
  mapping,
  reverse,
}: SortEventsOnGroupNumber): CalendarEvent[] {
  return events.sort((a, b) => {
    const aGroup = groups[a.data.groupId];
    const bGroup = groups[b.data.groupId];
    if (!isDefined(aGroup) || !isDefined(bGroup)) return 0;

    const aGroupNumber = mapping.parse("groupNumber", aGroup.teObject);
    const bGroupNumber = mapping.parse("groupNumber", bGroup.teObject);

    return reverse
      ? bGroupNumber.localeCompare(aGroupNumber)
      : aGroupNumber.localeCompare(bGroupNumber);
  });
}

type FindEventColor = {
  index: number;
};
export function getColorIndex({ index }: FindEventColor): number {
  if (index < 0) {
    return -1;
  }
  return index % nbrOfEventColors;
}

type ConflictProps = {
  registration: FrontendRegistration;
  event: CalendarEvent;
  mapping: Mapping;
  translations: RegistrationRequiredTranslations;
};

type DisplayAllConflictsReturn = (React.JSX.Element | null)[];
export function displayAllConflicts(
  props: ConflictProps
): DisplayAllConflictsReturn {
  const { event, registration } = props;

  // Conflicts that can be merged into one notification
  // Each entry in the object will be a different conflict notification
  const mergedConflicts = mergeConflicts({ event, registration });
  const conflicts = populateDaysOfMergedConflicts({
    mergedConflicts,
    registration,
  });

  return conflicts.map(({ key, reservationIds, days }) => {
    return displayConflict(props, {
      key,
      reservationIds: reservationIds ?? [],
      days,
    });
  });
}

type DisplayConflictReturn = React.JSX.Element | null;
function displayConflict(
  { registration, mapping }: ConflictProps,
  conflict: { key: string; reservationIds: number[]; days: Set<number> }
): DisplayConflictReturn {
  const { reservationIds, days } = conflict;

  // All conflict reservations have the same start and end time (day, hour and minute)
  // Also have the same course and activity type because it is the same group
  const event = registration.events.find((e) => e.id === reservationIds[0]);

  if (!isDefined(event)) return null;

  const start = dayjs(event.start.local);
  const end = dayjs(event.end.local);
  const day = start.format("ddd");
  const time = `${start.format("HH:mm")} - ${end.format("HH:mm")}`;
  const datesText = RangeReduce.daysPresent(days);

  const objectInfo =
    event.reservation.objects
      ?.map((o) => {
        const course = registration.courses[o.objectId];
        if (isDefined(course))
          return mapping.string("courseLabel", course.teObject);
        const group = registration.groups[o.objectId];
        if (isDefined(group))
          return mapping.string("activityType", group.teObject);
        return "";
      })
      .join(" ") ?? "";

  const text = ` with ${objectInfo.trim()}, ${day} ${time}, ${datesText} `;

  return (
    <ConflictNotification
      key={`conf_res_${conflict.key}`}
      ids={conflict.reservationIds}
      useText={true}
      text={text}
      className=""
    />
  );
}

type MergedConflictsProps = {
  event: CalendarEvent;
  registration: FrontendRegistration;
};
type MergeConflictsRecord = UnsafeRecord<string, number[]>;
/**
 * @description - This function is used to group conflicts with each other and which should be displayed on a different row.
 * Only includes conflicts that actually overlap in time with the expanded entry's reservations.
 * @param event - Calendar event to find conflicts with
 * @param registration - FrontendRegistration object
 * @returns Record with key as groupId and date based on day and time.
 * Value is an array of reservationIds that our group on date and time in key have conflicts with
 * Ex: {[myId-Thu 10:00-12:00]: [reservationId1, reservationId2]}
 */
export function mergeConflicts({
  event,
  registration,
}: MergedConflictsProps): MergeConflictsRecord {
  return event.data.reservations.reduce<UnsafeRecord<string, number[]>>(
    (mergedConflicts, reservation) => {
      const registrationEvent = registration.events.find(
        (e) => e.id === reservation.id
      );

      const allocationConflicts = registrationEvent?.allocationConflicts;

      if (!isDefined(allocationConflicts)) return mergedConflicts;

      for (const conflict of allocationConflicts) {
        const conflictEvent = registration.events.find(
          (e) => e.id === conflict.reservationId
        );
        if (!isDefined(conflictEvent)) continue;

        const hasTimeOverlap =
          hasTime(reservation) &&
          checkIsConflict(
            { start: conflictEvent.start.unix, end: conflictEvent.end.unix },
            { start: reservation.begin, end: reservation.end }
          );

        if (!hasTimeOverlap) continue;

        const { start, end } = conflictEvent;

        // The day, hour and minute (excluding weeks)
        const startDate = dayjs(start.local).format("ddd HH:mm");
        const endDate = dayjs(end.local).format("ddd HH:mm");
        const key = `${conflict.groupId} ${startDate} ${endDate}`;

        if (mergedConflicts[key] === undefined) {
          mergedConflicts[key] = [conflict.reservationId];
        } else {
          mergedConflicts[key]?.push(conflict.reservationId);
        }
      }

      return mergedConflicts;
    },
    {}
  );
}

type PopulateDaysOfMergedConflictsProps = {
  mergedConflicts: MergeConflictsRecord;
  registration: FrontendRegistration;
};
/**
 * @description - Add the day number when the conflict happens
 */
type PopulateDaysOfMergedConflictsReturn = {
  key: string;
  reservationIds: number[] | undefined;
  days: Set<number>;
}[];
export function populateDaysOfMergedConflicts({
  mergedConflicts,
  registration,
}: PopulateDaysOfMergedConflictsProps): PopulateDaysOfMergedConflictsReturn {
  return Object.entries(mergedConflicts).map(([key, reservationIds]) => {
    const days = new Set<number>();
    for (const id of reservationIds ?? []) {
      const registrationEvent = registration.events.find((e) => e.id === id);
      if (!isDefined(registrationEvent)) continue;
      days.add(dayjs(registrationEvent.start.local).startOf("day").unix());
    }
    return { key, reservationIds, days };
  });
}

/** The row's own group linked groups from other activities share the row but the controls bind to this one. */
export function findActivityOwnGroup(
  groups: FrontendRegistrationGroup[],
  activity: RegistrationActivity
): FrontendRegistrationGroup {
  const group = groups.find((g) => g.parentId === activity.id);
  if (!isDefined(group)) {
    throw new Error("[LogicError] Could not find any groups for activity");
  }
  return group;
}

/** aria-labelledby for a row's control: every group tag in the row plus the activity header. */
export function groupControlsLabelledBy(
  groups: FrontendRegistrationGroup[]
): string {
  return (
    groups.map((group) => `group-id-${group.id}`).join(" ") + " activity-header"
  );
}
